import { LitElement } from 'lit';
/**
 * Base class for all Band Way design system components.
 *
 * Conventions:
 * - Light DOM (no Shadow DOM) — tokens cascade, debugging easier,
 *   Storybook hot reload faster. Trade-off: every CSS selector inside
 *   a component must be prefixed with the component tag name (e.g.
 *   `bw-track-row .leftbar`). Enforced via ESLint rule in Phase 3.
 * - Lit reactive properties for inputs.
 * - CustomEvents (bubbling, composed) for outputs.
 * - Mode prop on compound components: 'edit' | 'readonly-promo'.
 *   Only readonly-promo implemented in this design-system-foundation
 *   project; edit reserved for future desktop migration.
 */
export class BwElement extends LitElement {
    constructor() {
        super(...arguments);
        /**
         * Snapshot of author-provided children captured BEFORE Lit renders into
         * the host. Light-DOM components opt in by calling `projectSlots()` in
         * their `firstUpdated()` to move these into the matching `<slot>`
         * positions inside the rendered template.
         *
         * Why: `<slot>` only projects content automatically in shadow DOM. In
         * light DOM it is just an inert placeholder, so author content sits
         * next to the rendered template instead of inside it (see B-054).
         */
        this._slottedDefault = null;
        this._slottedByName = null;
    }
    createRenderRoot() {
        return this;
    }
    update(changedProperties) {
        // Snapshot author-provided children BEFORE Lit writes the template
        // into the host. We can't do this in `connectedCallback` /
        // `createRenderRoot` because the HTML parser inserts the host element
        // first, runs the upgrade synchronously, and only THEN appends the
        // child nodes between the start and end tags. By the time of the
        // first `update()` (microtask after parsing finishes) the author
        // children are present and the template hasn't been rendered yet.
        if (this._slottedDefault === null) {
            this._captureSlottedChildren();
        }
        super.update(changedProperties);
    }
    _captureSlottedChildren() {
        if (this._slottedDefault !== null)
            return;
        this._slottedDefault = [];
        this._slottedByName = new Map();
        const children = Array.from(this.childNodes);
        for (const node of children) {
            if (node.nodeType === Node.ELEMENT_NODE &&
                node.hasAttribute('slot')) {
                const name = node.getAttribute('slot');
                const bucket = this._slottedByName.get(name) ?? [];
                bucket.push(node);
                this._slottedByName.set(name, bucket);
            }
            else {
                this._slottedDefault.push(node);
            }
        }
        // NB: we do NOT detach here. Lit appends the rendered template AFTER
        // existing children, so author content sits next to (not inside) the
        // template. `projectSlots()` reads from this snapshot and moves the
        // nodes into the slot position — `insertBefore` auto-detaches from
        // the old parent, so non-opting-in subclasses keep their template
        // children intact (e.g. parent renders `<bw-tag>NOW</bw-tag>`; the
        // inner bw-tag snapshots "NOW" but never projects).
    }
    /**
     * Replace each `<slot>` placeholder in the rendered template with the
     * matching author-provided children captured by `update()` snapshot.
     *
     * Call from `firstUpdated()` in subclasses that want light-DOM
     * slot projection (e.g. `bw-button`, `bw-tag`).
     */
    projectSlots() {
        if (!this._slottedDefault || !this._slottedByName)
            return;
        const slots = this.querySelectorAll('slot');
        slots.forEach((slot) => {
            const name = slot.getAttribute('name');
            const nodes = name == null
                ? this._slottedDefault
                : this._slottedByName.get(name) ?? [];
            const parent = slot.parentNode;
            if (!parent)
                return;
            for (const node of nodes) {
                parent.insertBefore(node, slot);
            }
            slot.remove();
        });
    }
}
//# sourceMappingURL=bw-element.js.map